import React from 'react';
import { ArrowLeft, Download, Star, Printer } from 'lucide-react';
import type { ReportData } from '../../types/reports';
import ChartContainer from '../dashboard/ChartContainer';

interface ReportViewerProps {
  report: ReportData;
  onBack: () => void;
  onExport: (format: 'pdf' | 'excel') => void;
  onToggleFavorite: (reportId: string) => void;
}

const ReportViewer: React.FC<ReportViewerProps> = ({
  report,
  onBack,
  onExport,
  onToggleFavorite
}) => {
  const renderReportContent = () => {
    switch (report.type) {
      case 'sales':
        return (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Total de Vendas
                </h3>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(report.data.totalSales)}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Ticket Médio
                </h3>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(report.data.averageTicket)}
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Total de Pedidos
                </h3>
                <p className="mt-2 text-3xl font-bold text-gray-900">
                  {report.data.totalOrders}
                </p>
              </div>
            </div>

            {/* Charts */}
            <div className="bg-white rounded-lg shadow">
              <ChartContainer
                title="Vendas por Período"
                type="bar"
                data={report.data.salesByPeriod}
                height={300}
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-gray-500">
              Visualização não disponível para este tipo de relatório.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {report.title}
              </h2>
              <p className="text-sm text-gray-500">
                {report.description}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => onToggleFavorite(report.id)}
              className={`p-2 rounded-full hover:bg-gray-100 ${
                report.favorite ? 'text-yellow-400' : 'text-gray-400'
              }`}
            >
              <Star className="h-5 w-5" />
            </button>
            <button
              onClick={() => onExport('pdf')}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={() => window.print()}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <Printer className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {renderReportContent()}
    </div>
  );
};

export default ReportViewer;